import React from 'react'
import { Chat } from './types'

interface ChatAvatarsProps {
  chat: Chat
  size?: number
}

interface StackedAvatarsProps {
  handles: Chat['chat_handles']
  size?: number
}

const getAvatarColor = (name: string): string => {
  const colors = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  ]

  if (!name) return colors[0]

  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + (hash * 32 - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

const getInitials = (name: string): string => {
  if (!name) return '?'

  const words = name.trim().split(' ')
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase()
  }

  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
}

const SingleAvatar: React.FC<{
  handle: Chat['chat_handles'][0]
  size: number
}> = ({ handle, size }) => {
  const imageUrl = handle.user_contact?.contact?.image_url
  const displayName = handle.display_name || handle.identifier

  if (imageUrl) {
    return (
      <div
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: size,
          height: size,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          border: '2px solid #454545',
        }}
      />
    )
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center"
      style={{
        width: size,
        height: size,
        background: getAvatarColor(displayName),
        border: '2px solid #454545',
        fontSize: Math.max(12, size * 0.35),
        fontWeight: '600',
        color: 'white',
      }}
    >
      {getInitials(displayName)}
    </div>
  )
}

const StackedAvatars: React.FC<StackedAvatarsProps> = ({
  handles,
  size = 48,
}) => {
  // Sort handles: those with photos first, then by ID for stability
  const sortedHandles = [...handles].sort((a, b) => {
    const aHasPhoto = !!a.user_contact?.contact?.image_url
    const bHasPhoto = !!b.user_contact?.contact?.image_url

    if (aHasPhoto !== bHasPhoto) {
      return aHasPhoto ? -1 : 1
    }

    return String(a.id).localeCompare(String(b.id))
  })

  const displayHandles = sortedHandles.slice(0, 2)
  const remainingCount = handles.length - displayHandles.length
  const avatarSize = size * 0.5

  return (
    <div
      className="position-relative rounded-circle d-flex align-items-center justify-content-center"
      style={{
        width: size,
        height: size,
        backgroundColor: '#2d2d2d',
        border: '2px solid #454545',
      }}
    >
      {displayHandles.map((handle, index) => (
        <div
          key={handle.id}
          className="position-absolute"
          style={{
            marginLeft: index > 0 ? -(avatarSize * 0.25) : 0,
            zIndex: displayHandles.length - index,
          }}
        >
          <SingleAvatar handle={handle} size={avatarSize} />
        </div>
      ))}

      {remainingCount > 0 && (
        <div
          className="position-absolute rounded-circle d-flex align-items-center justify-content-center"
          style={{
            width: Math.max(16, size * 0.3),
            height: Math.max(16, size * 0.3),
            backgroundColor: '#6c757d',
            color: 'white',
            fontSize: Math.max(10, size * 0.2),
            fontWeight: '600',
            bottom: -2,
            right: -2,
            border: '1px solid #454545',
          }}
        >
          {remainingCount > 99 ? '>99' : `+${remainingCount}`}
        </div>
      )}
    </div>
  )
}

const ChatAvatars: React.FC<ChatAvatarsProps> = ({ chat, size = 48 }) => {
  const nonMeHandles = chat.chat_handles.filter((handle) => !handle.is_me)

  // For individual chats, show single avatar
  if (!chat.group) {
    const otherHandle = nonMeHandles[0]
    if (!otherHandle) {
      // Fallback for edge case
      return (
        <div
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{
            width: size,
            height: size,
            backgroundColor: '#6c757d',
            border: '2px solid #454545',
            fontSize: Math.max(12, size * 0.35),
            fontWeight: '600',
            color: 'white',
          }}
        >
          ?
        </div>
      )
    }

    return <SingleAvatar handle={otherHandle} size={size} />
  }

  // For group chats, show stacked avatars
  return <StackedAvatars handles={nonMeHandles} size={size} />
}

export default ChatAvatars
